import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Building2, Home, MessagesSquare, MessageSquare, UserRound, Contact } from 'lucide-react'
import { platformAPI, supportChatsAPI, tenantDataAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useTenantDirectory } from '../lib/tenantDirectory'
import { makeMatcher, toTenantModel, tenantView, isMissingEndpoint, formatRelative, RANK, roleLabel } from '../lib/support'
import { Dot } from './ui'

// ⌘K / Ctrl+K palette. Prefers GET /api/platform/search (one round trip, server-side
// matching); while that endpoint is missing it searches client-side: tenants from the
// shared directory, tenant staff and recent support chats (both loaded once per palette
// session), and — owners only — a debounced per-tenant client search.

const PER_GROUP = 5
const CLIENT_FANOUT_MAX_TENANTS = 30
let unifiedSearchMissing = false
let chatsCache = { at: 0, list: null }
let staffCache = { at: 0, byTenant: null }
const CACHE_MS = 120_000

const loadChats = async () => {
  if (chatsCache.list && Date.now() - chatsCache.at < CACHE_MS) return chatsCache.list
  const res = await supportChatsAPI.getAll({ per_page: 100 })
  chatsCache = { at: Date.now(), list: res.data?.data || [] }
  return chatsCache.list
}

const loadStaff = async (tenants) => {
  if (staffCache.byTenant && Date.now() - staffCache.at < CACHE_MS) return staffCache.byTenant
  const results = await Promise.allSettled(
    tenants.map((t) => tenantDataAPI.getTenantUsers(t.id, { per_page: 100 }))
  )
  const byTenant = {}
  results.forEach((r, i) => {
    byTenant[tenants[i].id] = r.status === 'fulfilled' ? r.value.data?.data || [] : []
  })
  staffCache = { at: Date.now(), byTenant }
  return byTenant
}

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

export const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate()
  const { isOwner } = useAuth()
  const { tenants } = useTenantDirectory()
  const inputRef = useRef(null)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [chats, setChats] = useState(chatsCache.list || [])
  const [staff, setStaff] = useState(staffCache.byTenant || {})
  const [clientHits, setClientHits] = useState([])
  const [unified, setUnified] = useState(null)
  const debounced = useDebounced(query.trim(), 200)

  const views = useMemo(() => tenants.map((t) => tenantView(toTenantModel(t))), [tenants])
  const tenantName = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t.name])), [tenants])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
    setClientHits([])
    setUnified(null)
    requestAnimationFrame(() => inputRef.current?.focus())
    loadChats().then(setChats).catch(() => {})
  }, [open])

  // Staff lists are only needed for the client-side fallback.
  useEffect(() => {
    if (!open || !debounced || !unifiedSearchMissing || !tenants.length) return
    loadStaff(tenants).then(setStaff).catch(() => {})
  }, [open, debounced, tenants])

  useEffect(() => {
    if (!open || !debounced) {
      setUnified(null)
      setClientHits((prev) => (prev.length ? [] : prev))
      return
    }
    const controller = new AbortController()
    const run = async () => {
      if (!unifiedSearchMissing) {
        try {
          const res = await platformAPI.search(debounced, { signal: controller.signal })
          setUnified(res.data?.data || null)
          return
        } catch (err) {
          if (controller.signal.aborted) return
          if (!isMissingEndpoint(err)) return
          unifiedSearchMissing = true
          loadStaff(tenants).then(setStaff).catch(() => {})
        }
      }
      // Fallback: clients are searched server-side per tenant (owner only).
      if (!isOwner || debounced.length < 3 || tenants.length > CLIENT_FANOUT_MAX_TENANTS) return
      const results = await Promise.allSettled(
        tenants.map((t) =>
          tenantDataAPI.getTenantClients(
            t.id,
            { search: debounced, per_page: 5, include_deleted: 'true' },
          )
        )
      )
      if (controller.signal.aborted) return
      setClientHits(
        results.flatMap((r, i) =>
          r.status === 'fulfilled' ? (r.value.data?.data || []).map((c) => ({ ...c, tenant_id: tenants[i].id })) : []
        )
      )
    }
    run()
    return () => controller.abort()
  }, [open, debounced, isOwner, tenants])

  const go = (to) => {
    onClose()
    navigate(to)
  }

  const groups = useMemo(() => {
    const q = query.trim()
    if (!q) {
      const attention = views
        .filter((v) => v.needsAttention)
        .sort((a, b) => RANK[b.worst] - RANK[a.worst])
        .slice(0, 4)
      return [
        {
          label: 'Precisam de atenção',
          items: attention.map((v) => ({
            key: `a-${v.model.id}`,
            icon: Building2,
            title: v.model.name,
            sub: v.healthLabel,
            tone: v.worst,
            to: `/tenants/${v.model.id}`,
          })),
        },
        {
          label: 'Ir para',
          items: [
            { key: 'j-today', icon: Home, title: 'Hoje', sub: 'Pendências e conversas recentes', to: '/' },
            { key: 'j-tenants', icon: Building2, title: 'Estúdios', sub: 'Todos os estúdios', to: '/tenants' },
            { key: 'j-inbox', icon: MessagesSquare, title: 'Conversas de suporte', sub: 'Caixa de entrada e visão geral por IA', to: '/support-analysis' },
          ],
        },
      ].filter((g) => g.items.length)
    }

    const has = makeMatcher(q)
    const viewById = Object.fromEntries(views.map((v) => [v.model.id, v]))
    const tenantItem = (v) => ({
      key: `t-${v.model.id}`,
      icon: Building2,
      title: v.model.name,
      sub: `${v.model.slug} · ${v.model.plan} · ${v.healthLabel}`,
      tone: v.worst,
      to: `/tenants/${v.model.id}`,
    })
    const personItem = (u, tenantId) => ({
      key: `p-${tenantId}-${u.id || u.email}`,
      icon: UserRound,
      title: u.name || u.email,
      sub: `${u.email} · ${roleLabel(u.role)} em ${tenantName[tenantId] || 'estúdio'}${u.can_login === false ? ' · não consegue entrar' : ''}`,
      tone: u.can_login === false ? 'crit' : null,
      to: `/tenants/${tenantId}?tab=access`,
    })
    const clientItem = (c) => ({
      key: `c-${c.tenant_id}-${c.id}`,
      icon: Contact,
      title: c.name || c.email || c.phone,
      sub: `Cliente de ${tenantName[c.tenant_id] || 'estúdio'} · ${[c.email, c.phone].filter(Boolean).join(' · ')}`,
      to: `/tenants/${c.tenant_id}?tab=clients&q=${encodeURIComponent(c.email || c.phone || c.name || '')}${
        c.deleted_at ? '&deleted=1' : ''
      }`,
    })
    const chatItem = (c) => {
      const first = c.messages?.find((m) => m.role === 'user')?.content || c.first_message || ''
      return {
        key: `ch-${c.id}`,
        icon: MessageSquare,
        title: c.user_name || (c.chat_type === 'public' ? 'Visitante da documentação' : 'Usuário desconhecido'),
        sub: `${first ? `“${first}” · ` : ''}${formatRelative(c.created_at)}`,
        to: `/support-analysis?chat=${c.id}`,
      }
    }

    let sets
    if (unified) {
      sets = [
        ['Estúdios', (unified.tenants || []).map((t) => tenantItem(viewById[t.id] || tenantView(toTenantModel(t))))],
        ['Pessoas', (unified.users || []).map((u) => personItem(u, u.tenant_id))],
        ['Clientes', isOwner ? (unified.clients || []).map(clientItem) : []],
        ['Conversas de suporte', (unified.chats || []).map(chatItem)],
      ]
    } else {
      const people = Object.entries(staff).flatMap(([tid, list]) =>
        list.filter((u) => has(u.name, u.email)).map((u) => personItem(u, tid))
      )
      // Before staff lists load, owner emails from the tenant list still find people.
      const owners = Object.keys(staff).length
        ? []
        : tenants
            .filter((t) => t.owner_email && has(t.owner_email))
            .map((t) => personItem({ email: t.owner_email, role: 'owner' }, t.id))
      sets = [
        ['Estúdios', views.filter((v) => has(v.model.name, v.model.slug, v.model.billingEmail, v.model.domain)).map(tenantItem)],
        ['Pessoas', [...people, ...owners]],
        ['Clientes', isOwner ? clientHits.map(clientItem) : []],
        [
          'Conversas de suporte',
          chats
            .filter((c) => has(c.user_name, c.user_email, ...(c.messages || []).map((m) => m.content)))
            .map(chatItem),
        ],
      ]
    }
    return sets.filter(([, items]) => items.length).map(([label, items]) => ({ label, items: items.slice(0, PER_GROUP) }))
  }, [query, views, unified, staff, tenants, chats, clientHits, isOwner, tenantName])

  const flat = groups.flatMap((g) => g.items)
  useEffect(() => setActive(0), [query])

  if (!open) return null

  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose()
    else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(flat.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter' && flat[active]) {
      go(flat[active].to)
    }
  }

  const q = query.trim()
  let index = -1

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(15,23,42,.45)] px-4 py-[10vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Pesquisar"
        className="flex w-full max-w-[640px] flex-col overflow-hidden rounded-lg bg-admin-50 shadow-palette"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-admin-200 px-5 py-3.5">
          <Search className="h-4 w-4 text-admin-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Estúdio, e-mail, telefone, slug, texto da conversa…"
            aria-label="Pesquisar estúdios, pessoas, clientes e conversas"
            className="flex-1 border-0 bg-transparent text-base text-admin-900 outline-none placeholder:text-admin-500 focus-visible:outline-none"
          />
          <span className="rounded-md border border-admin-200 px-2 py-0.5 text-[11px] text-admin-500">esc</span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {groups.map((g) => (
            <div key={g.label} className="flex flex-col gap-0.5 py-1">
              <span className="micro-label px-3 py-1.5">{g.label}</span>
              {g.items.map((r) => {
                index += 1
                const highlighted = q && index === active
                const Icon = r.icon
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => go(r.to)}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2 text-left ${
                      highlighted ? 'border-admin-200 bg-white' : 'border-transparent hover:border-admin-200 hover:bg-white'
                    }`}
                  >
                    <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full border border-admin-200 bg-white">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col leading-snug">
                      <span className="font-semibold">{r.title}</span>
                      <span className="truncate text-xs text-admin-600">{r.sub}</span>
                    </span>
                    {r.tone && <Dot tone={r.tone} />}
                  </button>
                )
              })}
            </div>
          ))}
          {q && groups.length === 0 && (
            <p className="px-3 py-5 text-admin-600">
              Nada corresponde a “{q}”. Tente um telefone sem espaços ou parte de um e-mail.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
